import { Dropdown, Modal } from '@triumph/design-system';
import { useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { useAbandonApplicantMutation } from '@/components/shared/api/mutations/applicant';

import { MessageToApplicantTextArea, ModalContainer, ProductRow, DropdownWrapper } from '../../../pages/Applicant/components/ApplicantStatus/ApplicantStatus.StyledComponents';

interface AbandonApplicantFormData {
    product: string;
    reason: string;
}

const validateAbandonApplicantForm = () =>
    yup.object({
        product: yup.string().required('This field is required.'),
        reason: yup.string().required('This field is required.'),
    });

export const AbandonApplicantModal = ({
    showModal,
    setShowModal,
    onSuccess,
}: {
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    onSuccess: () => void;
}) => {
    const { applicantId, applicantProductNames } = useApplicantContext();

    const { mutateAsync: submitAbandonApplicant, isPending: isSubmitAbandonApplicantPending } =
        useAbandonApplicantMutation();

    const {
        control,
        handleSubmit,
        register,
        reset,
        setValue,
        watch,
    } = useForm<AbandonApplicantFormData>({
        resolver: yupResolver(validateAbandonApplicantForm()),
        defaultValues: {
            product: '',
            reason: '',
        },
    });

    const selectedProduct = watch('product');

    const {
        fieldState: { error: reasonError },
    } = useController({
        control,
        name: 'reason',
    });

    const productNameOptions = useMemo(() => {
        return applicantProductNames.map((product) => ({
            id: product,
            value: product,
            defaultSelected: product === selectedProduct,
        }));
    }, [selectedProduct, applicantProductNames]);

    const onSubmit = async (formData: AbandonApplicantFormData) => {
        if (applicantId && !isSubmitAbandonApplicantPending) {
            await submitAbandonApplicant({
                applicantId,
                product: formData.product as ProductNames,
                reason: formData.reason,
            });
            handleModalClose();
            onSuccess();
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        reset();
    };

    const handleProductChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setValue('product', event.target.value);
    };

    return (
        <Modal
            bodyContent={
                <ModalContainer>
                    <ProductRow>
                        <DropdownWrapper>
                            <Dropdown
                                id="dropdown-abandon-product"
                                options={productNameOptions}
                                labelText="Select Product Application"
                                selectTestId="dropdown-abandon-product"
                                onChange={handleProductChange}
                                selectedValue={selectedProduct}
                            />
                        </DropdownWrapper>
                    </ProductRow>
                    <MessageToApplicantTextArea
                        {...register('reason')}
                        rows={5}
                        label="Reason for abandoning"
                        errorMsg={reasonError?.message}
                        data-testid="abandon-reason"
                        spellCheck
                    />
                </ModalContainer>
            }
            id="abandon-applicant-modal"
            headerContent="Abandon Applicant"
            onClose={handleModalClose}
            visible={showModal}
            primaryButtonText="Abandon"
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryAdditionalProps={{
                loading: isSubmitAbandonApplicantPending,
                disabled: isSubmitAbandonApplicantPending,
            }}
            secondaryButtonText="Cancel"
            onSecondaryAction={handleModalClose}
        />
    );
};
